import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { verifyEmailToken } from "../services/api";

function EmailVerification() {
  const [verificationStatus, setVerificationStatus] = useState("processing"); // "processing", "success", "failed", "error"
  const [isLoading, setIsLoading] = useState(true);
  const [countdown, setCountdown] = useState(5); // Compte à rebours de 5 secondes
  const navigate = useNavigate();
  const location = useLocation();

  // Effet pour la vérification d'email
  useEffect(() => {
    const verifyEmail = async () => {
      const params = new URLSearchParams(location.search);
      const token = params.get("token");

      if (!token) {
        setVerificationStatus("failed");
        setIsLoading(false);
        return;
      }

      try {
        const response = await verifyEmailToken(token);

        if (response.data.success) {
          setVerificationStatus("success");
          toast.success("Votre email a été vérifié avec succès!");
        } else {
          setVerificationStatus("failed");
          toast.error("La vérification a échoué. Lien invalide ou expiré.");
        }
      } catch (error) {
        console.error("Erreur lors de la vérification:", error);
        if (error.response?.status === 400) {
          setVerificationStatus("failed");
          toast.error("La vérification a échoué. Lien invalide ou expiré.");
        } else {
          setVerificationStatus("error");
          toast.error("Une erreur est survenue. Veuillez réessayer plus tard.");
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [location, navigate]);

  // Effet pour le compte à rebours et la redirection si la vérification est réussie
  useEffect(() => {
    let timer;

    if (verificationStatus === "success" && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown((prevCount) => prevCount - 1);
      }, 1000);
    } else if (verificationStatus === "success" && countdown === 0) {
      navigate("/login?verification=success");
    }

    return () => {
      clearTimeout(timer);
    };
  }, [verificationStatus, countdown, navigate]);

  // Affichage pendant le chargement
  if (isLoading) {
    return (
      <div className="container d-flex align-items-center justify-content-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <h3 className="mt-3">Vérification de votre email en cours...</h3>
          <p className="text-muted">Veuillez patienter un instant</p>
        </div>
      </div>
    );
  }

  // Composant pour le compte à rebours
  const CountdownCircle = ({ seconds }) => {
    const percentage = (seconds / 5) * 100; // 5 étant le temps total
    const radius = 30;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (percentage / 100) * circumference;

    return (
      <div className="position-relative d-inline-block my-3">
        <svg width="80" height="80" viewBox="0 0 80 80">
          {/* Cercle de fond */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke="#e9ecef"
            strokeWidth="8"
          />
          {/* Cercle de progression */}
          <circle
            cx="40"
            cy="40"
            r={radius}
            fill="none"
            stroke="#198754"
            strokeWidth="8"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            transform="rotate(-90 40 40)"
            style={{ transition: "stroke-dashoffset 0.3s ease" }}
          />
        </svg>
        <span className="position-absolute top-50 start-50 translate-middle fw-bold fs-4">
          {seconds}
        </span>
      </div>
    );
  };

  // Affichage selon le statut
  let content;
  switch (verificationStatus) {
    case "success":
      content = (
        <div className="alert alert-success bg-success-subtle border-success text-center p-4">
          <i className="bi bi-check-circle-fill fs-1 d-block mb-3 text-success"></i>
          <h4>Email vérifié avec succès!</h4>
          <p className="mb-0">
            Votre adresse email a été vérifiée. Vous allez être redirigé vers la
            page de connexion dans :
          </p>

          <CountdownCircle seconds={countdown} />

          <div>
            <Link
              to="/login"
              className="btn btn-success mt-2"
              onClick={(e) => e.preventDefault()}
            >
              Redirection en cours...
            </Link>
          </div>
        </div>
      );
      break;
    case "failed":
      content = (
        <div className="alert alert-danger bg-danger-subtle border-danger text-center p-4">
          <i className="bi bi-x-circle-fill fs-1 d-block mb-3 text-danger"></i>
          <h4>Vérification échouée</h4>
          <p>
            Le lien de vérification est invalide ou a expiré. Veuillez demander
            un nouveau lien.
          </p>
          <Link to="/login" className="btn btn-primary mt-3">
            Retour à la connexion
          </Link>
        </div>
      );
      break;
    case "error":
      content = (
        <div className="alert alert-warning bg-warning-subtle border-warning text-center p-4">
          <i className="bi bi-exclamation-triangle-fill fs-1 d-block mb-3 text-warning"></i>
          <h4>Une erreur est survenue</h4>
          <p>
            Nous n'avons pas pu vérifier votre email. Veuillez réessayer plus
            tard ou contacter notre support.
          </p>
          <Link to="/login" className="btn btn-primary mt-3">
            Retour à la connexion
          </Link>
        </div>
      );
      break;
    default:
      content = null;
  }

  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100">
      <div
        className="card shadow-lg border-0 rounded-3"
        style={{ maxWidth: "500px", width: "100%" }}
      >
        <div className="card-body p-4 p-lg-5">
          <div className="text-center mb-4">
            <i className="bi bi-envelope-check-fill fs-1 text-primary"></i>
            <h2 className="card-title mt-3">Vérification de l'email</h2>
          </div>

          {content}
        </div>
      </div>
    </div>
  );
}

export default EmailVerification;
